import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Close as CloseIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import { useState } from "react";
import { RTL } from "../../cache/CacheRtl";
import CompanyMultiSelectDialog from "../shared/CompanyMultiSelectDialog";
import { sendAdminMessage } from "../../services/AdminMessageService";
import { useMessageContext } from "../../contexts/MessageContext";
import { borders, colors, shadows } from "../ui/colors";

const SendAdminMessageDialog = ({ isOpen, onClose, onSent }) => {
  const { notificationHandler } = useMessageContext();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [isAllCompanies, setIsAllCompanies] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const reset = () => {
    setTitle("");
    setContent("");
    setSelectedCompanies([]);
    setIsAllCompanies(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleCompanyConfirm = (companies, allFlag) => {
    setSelectedCompanies(companies);
    setIsAllCompanies(allFlag);
    setCompanyDialogOpen(false);
  };

  const recipientsLabel = isAllCompanies
    ? "כל החברות"
    : selectedCompanies.length > 0
      ? `${selectedCompanies.length} חברות נבחרו`
      : "לא נבחרו נמענים";

  const canSubmit = title.trim() && content.trim() && (isAllCompanies || selectedCompanies.length > 0);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await sendAdminMessage({
        title: title.trim(),
        content: content.trim(),
        companyIds: isAllCompanies ? [] : selectedCompanies.map(c => c.id),
        targetAllCompanies: isAllCompanies,
      });
      notificationHandler.success("ההודעה נשלחה");
      reset();
      onSent && onSent();
      onClose();
    } catch (err) {
      notificationHandler.error("שליחת ההודעה נכשלה");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={handleClose}
        PaperProps={{
          sx: {
            borderRadius: borders.radius.xl,
            minWidth: { xs: "95%", md: "560px" },
            maxWidth: { xs: "95%", md: "640px" },
            boxShadow: shadows.lg,
          },
        }}
      >
        <RTL>
          <Box dir="rtl">
            <Box sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              p: 2,
              borderBottom: borders.default,
              backgroundColor: colors.bg,
            }}>
              <Typography sx={{ fontWeight: 700, fontSize: "18px", color: colors.textPrimary }}>
                שליחת הודעה לחברות
              </Typography>
              <IconButton size="small" onClick={handleClose} disabled={submitting}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="כותרת"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                size="small"
                fullWidth
                inputProps={{ maxLength: 255 }}
              />

              <TextField
                label="תוכן ההודעה"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                multiline
                minRows={5}
                fullWidth
              />

              <Box>
                <Typography sx={{ fontSize: "13px", color: colors.textSecondary, mb: 1, fontWeight: 600 }}>
                  נמענים
                </Typography>
                <Box sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  flexWrap: "wrap",
                  p: 1.5,
                  border: borders.default,
                  borderRadius: borders.radius.md,
                  backgroundColor: colors.bg,
                }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setCompanyDialogOpen(true)}
                    startIcon={<BusinessIcon />}
                    sx={{
                      borderColor: colors.primary,
                      color: colors.primary,
                      "&:hover": { backgroundColor: colors.primarySubtle },
                      textTransform: "none",
                    }}
                  >
                    בחר חברות
                  </Button>
                  <Typography sx={{ fontSize: "13px", color: colors.textSecondary }}>
                    {recipientsLabel}
                  </Typography>
                  {!isAllCompanies && selectedCompanies.length > 0 && (
                    <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", width: "100%", mt: 1 }}>
                      {selectedCompanies.map((c) => (
                        <Chip
                          key={c.id}
                          label={c.name}
                          size="small"
                          sx={{ backgroundColor: colors.primarySubtle, color: colors.primary }}
                        />
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            <Box sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: borders.default,
            }}>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={submitting}
                sx={{ textTransform: "none" }}
              >
                ביטול
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                startIcon={submitting ? null : <SendIcon />}
                sx={{
                  backgroundColor: colors.primary,
                  "&:hover": { backgroundColor: colors.primaryDark },
                  textTransform: "none",
                  minWidth: "120px",
                }}
              >
                {submitting ? <CircularProgress size={20} sx={{ color: "white" }} /> : "שלח"}
              </Button>
            </Box>
          </Box>
        </RTL>
      </Dialog>

      <CompanyMultiSelectDialog
        isOpen={companyDialogOpen}
        onClose={() => setCompanyDialogOpen(false)}
        onConfirm={handleCompanyConfirm}
        title="בחירת חברות לשליחה"
        confirmButtonText="אישור"
        initialSelectedCompanies={selectedCompanies}
        initialIsAllCompanies={isAllCompanies}
      />
    </>
  );
};

export default SendAdminMessageDialog;
